import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { useI18n } from "../i18n/LanguageContext";
import BrandMark from "./BrandMark";
import InstallAppButton from "./InstallAppButton";
import LanguageSwitcher from "./LanguageSwitcher";

const ADMIN_LINKS = [
  { to: "/admin", label: "Cruscotto", testid: "nav-admin" },
  { to: "/admin/funnel", label: "Funnel", testid: "nav-admin-funnel" },
  { to: "/admin/clients", label: "Clienti", testid: "nav-clients" },
  { to: "/admin/invites", label: "Inviti", testid: "nav-invites" },
  { to: "/admin/deposits", label: "Depositi", testid: "nav-deposits" },
  { to: "/admin/statements", label: "Estratti", testid: "nav-statements" },
  { to: "/admin/kyc", label: "KYC", testid: "nav-admin-kyc" },
  { to: "/admin/kyc-reminders", label: "Solleciti", testid: "nav-admin-kyc-reminders" },
  { to: "/admin/events", label: "Eventi", testid: "nav-admin-events" },
  { to: "/admin/plans", label: "Pacchetti", testid: "nav-admin-plans" },
  { to: "/admin/investment-requests", label: "Richieste", testid: "nav-admin-requests" },
  { to: "/admin/introducers", label: "Introducer", testid: "nav-admin-introducers" },
  { to: "/admin/team", label: "Team", testid: "nav-admin-team" },
  { to: "/admin/security", label: "Sicurezza", testid: "nav-admin-security" },
  { to: "/admin/password-audit", label: "Audit", testid: "nav-admin-password-audit" },
];

const clientLinks = (t) => [
  { to: "/dashboard", label: t("nav.dashboard"), testid: "nav-dashboard" },
  { to: "/positions", label: t("nav.positions"), testid: "nav-positions" },
  { to: "/statements", label: t("nav.statements"), testid: "nav-statements-client" },
  { to: "/kyc", label: t("nav.kyc"), testid: "nav-kyc" },
  { to: "/events", label: t("nav.events"), testid: "nav-events" },
  { to: "/plans", label: t("nav.plans"), testid: "nav-plans" },
  { to: "/explore", label: t("nav.explore"), testid: "nav-explore", matchSubpath: true },
];

const introducerLinks = (t) => [
  { to: "/introducer", label: t("intro.nav_dashboard"), testid: "nav-introducer-dashboard" },
  { to: "/introducer/referrals", label: t("intro.nav_referrals"), testid: "nav-introducer-referrals" },
  { to: "/introducer/contract", label: t("intro.nav_contract"), testid: "nav-introducer-contract" },
  { to: "/introducer/guide", label: t("intro.nav_guide"), testid: "nav-introducer-guide" },
  { to: "/plans", label: t("nav.plans"), testid: "nav-introducer-plans" },
  { to: "/events", label: t("nav.events"), testid: "nav-introducer-events" },
  { to: "/explore", label: t("nav.explore"), testid: "nav-introducer-explore", matchSubpath: true },
];

export default function PortalHeader() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const isAdmin = user && user.role === "admin";
  const isIntroducer = user && user.role === "introducer";
  const links = isAdmin
    ? ADMIN_LINKS
    : isIntroducer
      ? introducerLinks(t)
      : clientLinks(t);
  const homePath = isAdmin ? "/admin" : isIntroducer ? "/introducer" : "/dashboard";

  return (
    <header
      className="fixed top-0 left-0 right-0 z-40 backdrop-blur-md"
      style={{
        background: "rgba(10,14,26,0.78)",
        borderBottom: "1px solid var(--br-line)",
      }}
      data-testid="portal-header"
    >
      <div className="max-w-[1280px] mx-auto px-4 sm:px-8">
        <div className="flex items-center justify-between h-[64px] sm:h-[72px] gap-3">
          <Link to={homePath} className="flex items-center gap-3 min-w-0" data-testid="brand-link">
            <BrandMark size={32} />
            <div className="leading-tight min-w-0">
              <div className="font-serif-display italic text-[1.1rem] sm:text-[1.25rem] text-[color:var(--br-parchment)] truncate">
                Borealis <span className="text-[color:var(--br-bronze-soft)]">Reserve</span>
              </div>
              <div className="hidden xs:block font-mono-fin text-[0.56rem] tracking-[0.36em] uppercase text-[color:var(--br-bronze)] mt-1">
                {t("header.private_portal")}
              </div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-7" data-testid="portal-nav">
            {links.map((l) => (
              <PortalLink key={l.to} to={l.to} testid={l.testid} matchSubpath={l.matchSubpath}>
                {l.label}
              </PortalLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0" data-testid="portal-user-block">
            <div className="hidden lg:flex flex-col items-end leading-tight">
              <span className="text-[0.78rem] text-[color:var(--br-parchment)]" data-testid="user-name">
                {user?.name}
              </span>
              <span className="font-mono-fin text-[0.62rem] tracking-[0.24em] uppercase text-[color:var(--br-bronze)]" data-testid="user-role">
                {user?.role}
              </span>
            </div>
            <div className="hidden md:block pl-2 border-l" style={{ borderColor: "var(--br-line)" }}>
              <LanguageSwitcher variant="header" />
            </div>
            <button
              onClick={handleLogout}
              className="br-btn-outline hidden md:inline-flex"
              data-testid="logout-button"
              style={{ padding: "10px 16px" }}
            >
              {isAdmin ? "Esci" : t("nav.logout")}
            </button>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="md:hidden inline-flex items-center justify-center h-11 w-11 border"
              style={{ borderColor: "var(--br-line-bright)", color: "var(--br-bronze)" }}
              aria-label="Menu"
              aria-expanded={menuOpen}
              data-testid="mobile-menu-toggle"
            >
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      {menuOpen ? (
        <div
          className="md:hidden overflow-y-auto"
          style={{
            background: "rgba(10,14,26,0.98)",
            borderTop: "1px solid var(--br-line)",
            maxHeight: "calc(100vh - 64px)",
          }}
          data-testid="mobile-menu"
        >
          <nav className="px-4 py-3" data-testid="mobile-nav">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={!l.matchSubpath}
                className={({ isActive }) =>
                  `block px-3 py-3.5 font-mono-fin text-[0.72rem] tracking-[0.2em] uppercase border-b transition-colors ${
                    isActive ? "text-[color:var(--br-bronze)]" : "text-[color:var(--br-text-dim)]"
                  }`
                }
                style={{ borderColor: "var(--br-line)" }}
                data-testid={`mobile-${l.testid}`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="px-7 py-4 flex items-center justify-between gap-4" style={{ borderBottom: "1px solid var(--br-line)" }}>
            <div className="leading-tight">
              <div className="text-[0.82rem] text-[color:var(--br-parchment)]">{user?.name}</div>
              <div className="font-mono-fin text-[0.6rem] tracking-[0.24em] uppercase text-[color:var(--br-bronze)] mt-0.5">
                {user?.role}
              </div>
            </div>
            <LanguageSwitcher variant="header" />
          </div>

          <div className="px-4 py-4 space-y-3">
            <InstallAppButton className="w-full" style={{ padding: "12px 16px" }} />
            <button
              onClick={handleLogout}
              className="br-btn-primary w-full"
              style={{ padding: "12px 16px" }}
              data-testid="mobile-logout-button"
            >
              {isAdmin ? "Esci" : t("nav.logout")}
            </button>
          </div>
        </div>
      ) : null}
    </header>
  );
}

function PortalLink({ to, children, testid, matchSubpath = false }) {
  return (
    <NavLink
      to={to}
      end={!matchSubpath}
      className={({ isActive }) =>
        `relative font-mono-fin text-[0.68rem] tracking-[0.18em] uppercase transition-colors duration-200 py-2 ${
          isActive ? "text-[color:var(--br-parchment)]" : "text-[color:var(--br-text-dim)] hover:text-[color:var(--br-parchment)]"
        }`
      }
      data-testid={testid}
    >
      {({ isActive }) => (
        <>
          {children}
          <span
            className="absolute left-0 bottom-0 h-px transition-all duration-300"
            style={{
              width: isActive ? "100%" : 0,
              background: "var(--br-bronze)",
            }}
          />
        </>
      )}
    </NavLink>
  );
}
